import { ThemeEvents } from '@theme/events';

/**
 * <variant-title> — renders the selected variant's title on a product page and swaps it live
 * when the shopper changes variant.
 *
 * Listens on `document` (not the closest section) so it works even when placed in a DIFFERENT
 * section from the variant picker — e.g. a hero title section above the main product section.
 * The variant:update event bubbles to document and carries the full variant object in
 * `event.detail.resource`, so we read `.title` directly and swap only the heading's text
 * (typography is CSS, so text-transform etc. re-apply). Reading resource.title — rather than the
 * picker section's re-rendered HTML — is what makes cross-section blocks update too.
 *
 * Product-id guard mirrors product-price.js: ignore updates from a different product's picker
 * (combined listings / other forms), and adopt the new product id on a combined-listing swap.
 *
 * Loaded block-locally (a <script> in blocks/variant-title.liquid) so it only ships where placed.
 */
class VariantTitle extends HTMLElement {
  connectedCallback() {
    document.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate);
  }

  disconnectedCallback() {
    document.removeEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate);
  }

  #onVariantUpdate = (event) => {
    if (event.detail?.data?.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (
      event.target instanceof HTMLElement &&
      event.target.dataset.productId &&
      event.target.dataset.productId !== this.dataset.productId
    ) {
      return;
    }

    const title = event.detail?.resource?.title;
    if (!title) return;

    const target = this.querySelector('[role="heading"]') ?? this;
    target.textContent = title;
  };
}

if (!customElements.get('variant-title')) {
  customElements.define('variant-title', VariantTitle);
}
